import { SheetGroup, LessonSheetData, DocumentData } from './lesson.constants';
import WorksheetCard from 'approot/shared/worksheet-card/worksheet-card';
import { ASSESSMENT, WORKSHEET, ContentDocumentType } from 'global/constants';
import { generateWorksheetDataAttributes } from './sheets.utils';
import {
  ContentCardRow,
  ContentCardCol,
  ContentCardColThirds,
} from 'approot/shared/content/content-row.style';
import { canDisableBookmark } from 'approot/shared/logic/can-disable-bookmark';
import { LessonActivitiesFragment_lessonActivities_studentPrintables } from './sections/activities/__generated__/LessonActivitiesFragment';
import { EVENT_CATEGORY_WORKSHEETS } from 'approot/shared/data-tracker/data-events.constants';

type SheetsProps = {
  lesson: LessonSheetData;
  sheetDefinitions: DocumentData[];
  sheetGroup: SheetGroup;
  isStudentView?: boolean;
  canBookmark?: boolean;
  fourColumnLayout?: boolean;
  handleBookmarkClick?: (
    page: number,
    isBookmarking: boolean,
    documentType?: ContentDocumentType
  ) => void;
  bookmarkedPages?: { pages: number[]; documentType?: ContentDocumentType }[];
  hasActivities?: boolean;
  studentPrintables?: LessonActivitiesFragment_lessonActivities_studentPrintables | null;
};

export const Sheets = ({
  lesson,
  sheetDefinitions,
  sheetGroup,
  isStudentView,
  canBookmark,
  fourColumnLayout,
  handleBookmarkClick,
  bookmarkedPages,
  hasActivities,
  studentPrintables,
}: SheetsProps) => {
  const isAssessment = lesson.type === ASSESSMENT;
  const contentLabel = isAssessment ? 'Assessments' : 'Lessons';

  const CardColumn = fourColumnLayout ? ContentCardCol : ContentCardColThirds;

  return (
    <ContentCardRow>
      {/* When student printables are defined the old worksheet will come back as null  */}
      {studentPrintables && (
        <CardColumn>
          <WorksheetCard
            {...generateWorksheetDataAttributes(
              lesson,
              EVENT_CATEGORY_WORKSHEETS,
              1,
              [],
              true
            )}
            useLazyLoad={false}
            isBookmarkable={false}
            link={studentPrintables.downloadUrl || undefined}
            title="Worksheets"
            imageUrl={studentPrintables.previewUrl}
            imageRatio={70}
            isAlwaysViewable={isStudentView ? isStudentView : false}
            type={WORKSHEET}
          />
        </CardColumn>
      )}

      {sheetDefinitions.map(sheetType => {
        const sheet = lesson[sheetType.key] && lesson[sheetType.key]!.document;

        if (!sheet || !sheet.individualPages) return null;

        const pages = sheetType.displaySingle
          ? [sheet.individualPages[0]]
          : sheet.individualPages;
        const pageNumbers = sheet.individualPages.map((p, i) => i + 1);

        return pages.map((pageResponsive, index) => {
          const bookmarks =
            bookmarkedPages &&
            bookmarkedPages.find(
              pages => pages.documentType === sheetType.documentType
            );
          const isBookmarked = !!(
            bookmarks && bookmarks.pages.includes(index + 1)
          );
          const pageUrl = sheetType.displaySingle
            ? sheet.downloadURL
            : pageResponsive.downloadURL;

          return (
            <CardColumn key={`${lesson.id}.${sheetType.key}${index}`}>
              <WorksheetCard
                {...generateWorksheetDataAttributes(
                  lesson,
                  sheetType.dataEvent,
                  index + 1,
                  pageNumbers,
                  sheetType.displaySingle
                )}
                isBookmarkable
                allowBookmark={!!(canBookmark && sheetType.canBookmark)}
                bookmarked={isBookmarked}
                disableBookmark={canDisableBookmark({
                  canBookmark: !!(canBookmark && sheetType.canBookmark),
                  canSeePublishedActivities: !!hasActivities,
                  isBookmarked,
                  documentType: sheetType.documentType,
                  isAssessment,
                })}
                disableBookmarkTooltipText={`We are phasing out Class To Do for our previous ${contentLabel}. Explore our new, enhanced ${contentLabel} ${
                  isAssessment ? '' : 'on the Lesson tab '
                }for an improved Class To Do experience!`}
                onBookmarkClick={() =>
                  handleBookmarkClick &&
                  handleBookmarkClick(
                    index + 1,
                    !isBookmarked,
                    sheetType.documentType
                  )
                }
                id={lesson.id}
                link={pageUrl || undefined}
                title={
                  sheetType.displaySingle || isAssessment
                    ? sheetType.name
                    : pageResponsive.pageLabel
                }
                // if sheet ratios haven't been determined yet, don't pass image urls to prevent image requests of the wrong size.
                images={
                  lesson.sheetPreviewRatio ? pageResponsive.pagesPreview : []
                }
                imageRatio={
                  lesson.sheetPreviewRatio &&
                  lesson.sheetPreviewRatio[sheetGroup]
                }
                isAlwaysViewable={isStudentView ? isStudentView : false}
                type={WORKSHEET}
              />
            </CardColumn>
          );
        });
      })}
    </ContentCardRow>
  );
};

export default Sheets;
